import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  Button,
  Tag,
  Table,
  message,
  Modal,
  Select,
  Progress,
  Alert,
  Spin,
  Typography,
  Space,
  Divider,
  Tooltip
} from 'antd';
import {
  ReloadOutlined,
  PlayCircleOutlined,
  ToolOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  InfoCircleOutlined,
  SyncOutlined,
  DesktopOutlined,
  GlobalOutlined
} from '@ant-design/icons';
import WaterLinkService from '../../services/WaterLinkService';
import ElectronWaterLinkService from '../../services/ElectronWaterLinkService';
import { lireDisqueDefaut, ecrireDisqueDefaut, resoudreDisque } from '../../services/spintouchConfig';

const { Title, Text } = Typography;
const { Option } = Select;

// Page officielle de telechargement du logiciel WaterLink Connect (LaMotte).
const URL_TELECHARGEMENT_WLC = 'https://lamotte.com/product/waterlink-connect-desktop-software/';

const SpintouchManagementAdaptive = () => {
  // États principaux
  const [loading, setLoading] = useState(false);
  const [serviceStatus, setServiceStatus] = useState(null);
  const [deviceStatus, setDeviceStatus] = useState(null);
  const [testOptions, setTestOptions] = useState(null);
  const [testResults, setTestResults] = useState(null);

  // États pour les tests
  const [selectedReagent, setSelectedReagent] = useState(null);
  const [selectedSample, setSelectedSample] = useState(null);
  const [testInProgress, setTestInProgress] = useState(false);
  const [testProgress, setTestProgress] = useState(0);

  // États pour les modales
  const [showTestModal, setShowTestModal] = useState(false);
  const [showResultsModal, setShowResultsModal] = useState(false);

  // Détecter l'environnement
  const [isElectron, setIsElectron] = useState(false);
  const [service, setService] = useState(null);

  // Encart dont le détail est déplié : 'waterlink' ou 'spintouch'.
  // WaterLink par défaut, c'est le premier maillon à vérifier en cas de panne.
  const [detailOuvert, setDetailOuvert] = useState('waterlink');
  const [derniereVerif, setDerniereVerif] = useState(null);
  const [erreurService, setErreurService] = useState(null);

  // Disque de reactif utilise par defaut lors d'une analyse depuis le
  // formulaire. Reglage local au poste, memorise dans le navigateur.
  const [disqueDefaut, setDisqueDefaut] = useState(lireDisqueDefaut());

  useEffect(() => {
    // Détecter si on est dans Electron
    const electronDetected = ElectronWaterLinkService.isElectronApp();
    setIsElectron(electronDetected);
    setService(electronDetected ? ElectronWaterLinkService : WaterLinkService);

    if (electronDetected) {
      console.log('[Spintouch] Mode Electron détecté');
    } else {
      console.log('[Spintouch] Mode navigateur détecté');
    }
  }, []);

  // Charger les données initiales
  useEffect(() => {
    if (service) {
      loadInitialData();
      // Actualiser toutes les 5 secondes
      const interval = setInterval(loadInitialData, 5000);
      return () => clearInterval(interval);
    }
  }, [service]);

  const loadInitialData = async () => {
    if (!service) return;

    setLoading(true);

    let infoService = null;
    let statut = null;

    // Seuls checkService et getStatus determinent l'etat de connexion affiche
    // par les deux encarts. Une remise a zero est indispensable ici : sans
    // elle, les valeurs de la derniere lecture reussie restaient affichees
    // alors que le service ne repondait plus.
    try {
      infoService = await service.checkService();
      statut = await service.getStatus();
      setErreurService(null);
    } catch (error) {
      setErreurService(error.message);
      // Pas de message.error : loadInitialData est rappele toutes les 5
      // secondes par le polling, ce qui empilait un toast rouge en continu.
      // L'etat est deja porte par l'encart WaterLink.
    }

    setServiceStatus(infoService);
    setDeviceStatus(statut);

    // Les options de test sont accessoires : leur echec ne doit pas faire
    // croire que le service est absent, d'ou ce try distinct.
    if (statut && statut.CanDoWatertest) {
      try {
        setTestOptions(await service.getOptions());
      } catch (error) {
        setTestOptions(null);
      }
    } else {
      setTestOptions(null);
    }

    setDerniereVerif(new Date());
    setLoading(false);
  };

  const handleRunTest = async () => {
    if (!service) return;

    if (selectedReagent == null || selectedSample == null) {
      message.warning('Veuillez sélectionner un réactif et un échantillon');
      return;
    }

    try {
      setTestInProgress(true);
      setTestProgress(0);

      // Simuler le progrès du test
      const progressInterval = setInterval(() => {
        setTestProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return prev;
          }
          return prev + 10;
        });
      }, 1000);

      const results = await service.runWaterTest(selectedReagent, selectedSample);

      clearInterval(progressInterval);
      setTestProgress(100);
      setTestResults(results);
      setShowTestModal(false);
      setShowResultsModal(true);

      message.success('Test terminé avec succès');

    } catch (error) {
      message.error('Erreur lors du test: ' + error.message);
    } finally {
      setTestInProgress(false);
      setTestProgress(0);
    }
  };

  const handleUpdateFirmware = async () => {
    if (!service) return;

    Modal.confirm({
      title: 'Mise à jour du firmware',
      content: 'Une mise à jour du firmware est disponible. Cette opération peut prendre 5-7 minutes et l\'appareil redémarrera plusieurs fois. Voulez-vous continuer ?',
      okText: 'Oui',
      cancelText: 'Non',
      onOk: async () => {
        try {
          setLoading(true);
          await service.updateFirmware();
          message.success('Mise à jour du firmware lancée');
          loadInitialData();
        } catch (error) {
          message.error('Erreur lors de la mise à jour: ' + error.message);
        } finally {
          setLoading(false);
        }
      }
    });
  };

  const handleCalibrate = async () => {
    if (!service) return;

    Modal.confirm({
      title: 'Calibration de l\'appareil',
      content: 'Veuillez placer le disque de vérification du compteur dans l\'appareil avant de lancer la calibration.',
      okText: 'Lancer la calibration',
      cancelText: 'Annuler',
      onOk: async () => {
        try {
          setLoading(true);
          await service.calibrate();
          message.success('Calibration lancée');
          loadInitialData();
        } catch (error) {
          message.error('Erreur lors de la calibration: ' + error.message);
        } finally {
          setLoading(false);
        }
      }
    });
  };

  const getCompatibleSamples = () => {
    if (!testOptions || selectedReagent == null) return [];

    const reagent = testOptions.ReagentTypes.find(r => r.Value === selectedReagent);
    if (!reagent) return [];

    return testOptions.SampleTypes.filter(sample =>
      sample.Tags.some(tag => reagent.Tags.includes(tag))
    );
  };

  // ---------------------------------------------------------------------------
  // Etat des deux maillons, presente sous forme de deux encarts cliquables.
  // Le service WaterLink Connect repond meme sans photometre branche : les deux
  // etats sont donc calcules separement et ne doivent jamais etre confondus.
  // ---------------------------------------------------------------------------

  const etatWaterLink = () => {
    if (serviceStatus) {
      return { couleur: '#52c41a', libelle: 'Connecté', ok: true };
    }
    return { couleur: '#ff4d4f', libelle: 'Introuvable', ok: false };
  };

  const etatSpintouch = () => {
    if (!serviceStatus) {
      return { couleur: '#bfbfbf', libelle: 'Indéterminé' };
    }
    const code = deviceStatus ? deviceStatus.Status : undefined;
    if (code === 5) {
      return { couleur: '#52c41a', libelle: 'Prêt' };
    }
    if (code === 0 || code === undefined) {
      return { couleur: '#faad14', libelle: 'Non branché' };
    }
    return { couleur: '#faad14', libelle: service.getStatusText(code) };
  };

  const renderEncart = (cle, titre, sousTitre, etat) => {

    const selectionne = detailOuvert === cle;

    return (
      <Card
        size="small"
        hoverable
        onClick={() => setDetailOuvert(cle)}
        style={{
          cursor: 'pointer',
          borderColor: selectionne ? '#0093CF' : undefined,
          borderWidth: selectionne ? 2 : 1,
          height: '100%'
        }}
      >
        <Space align="start">
          <span
            style={{
              display: 'inline-block',
              width: 14,
              height: 14,
              borderRadius: '50%',
              backgroundColor: etat.couleur,
              marginTop: 4
            }}
          />
          <div>
            <div><Text strong>{titre}</Text></div>
            <div><Text type="secondary" style={{ fontSize: 12 }}>{sousTitre}</Text></div>
            <div style={{ marginTop: 4 }}><Text>{etat.libelle}</Text></div>
          </div>
        </Space>
      </Card>
    );
  };

  const renderDetailWaterLink = () => (
    <Card title="Logiciel WaterLink Connect" size="small">
      {serviceStatus ? (
        <Space direction="vertical" size="small">
          <div>
            <Text strong>Version : </Text>
            <Text>{serviceStatus.version}</Text>
          </div>
          <div>
            <Text strong>Documentation : </Text>
            <Text>{serviceStatus.documentation}</Text>
          </div>
        </Space>
      ) : (
        <Space direction="vertical" size="small">
          <Text>
            Le logiciel WaterLink Connect doit être installé et démarré sur ce
            poste pour dialoguer avec le photomètre.
          </Text>
          <a href={URL_TELECHARGEMENT_WLC} target="_blank" rel="noopener noreferrer">
            Télécharger WaterLink Connect
          </a>
          {erreurService && (
            <Text type="secondary">Détail technique : {erreurService}</Text>
          )}
        </Space>
      )}
    </Card>
  );

  const renderDeviceStatus = () => {
    if (!deviceStatus) {
      return (
        <Card title="Photomètre Spin Touch" size="small">
          <Text>
            {serviceStatus
              ? "Aucun photomètre n'est branché. Raccordez le Spin Touch en USB, allumez-le, puis actualisez."
              : "État indisponible tant que WaterLink Connect ne répond pas."}
          </Text>
        </Card>
      );
    }

    const statusText = service.getStatusText(deviceStatus.Status);
    const statusColor = service.getStatusColor(deviceStatus.Status);

    return (
      <Card title="Photomètre Spin Touch" size="small">
        <Row gutter={[16, 16]}>
          <Col span={12}>
            <Space direction="vertical" size="small">
              <div>
                <Text strong>Nom: </Text>
                <Text>{deviceStatus.Name || 'Non connecté'}</Text>
              </div>
              <div>
                <Text strong>Statut: </Text>
                <Tag color={statusColor}>{statusText}</Tag>
              </div>
              <div>
                <Text strong>Firmware actuel: </Text>
                <Text>{deviceStatus.CurrentFirmwareString || 'N/A'}</Text>
              </div>
            </Space>
          </Col>
          <Col span={12}>
            <Space direction="vertical" size="small">
              <div>
                <Text strong>Firmware disponible: </Text>
                <Text>{deviceStatus.AvailFirmwareString || 'N/A'}</Text>
              </div>
              <div>
                <Text strong>Progrès: </Text>
                <Text>{deviceStatus.Progress}%</Text>
              </div>
              <div>
                <Text strong>Capacités: </Text>
                <Space wrap>
                  {deviceStatus.CanDoWatertest && <Tag color="green">Test d'eau</Tag>}
                  {deviceStatus.CanGetLog && <Tag color="blue">Logs</Tag>}
                  {deviceStatus.CanDoUpdate && <Tag color="orange">Mise à jour</Tag>}
                  {deviceStatus.CanDoCalibrate && <Tag color="purple">Calibration</Tag>}
                </Space>
              </div>
            </Space>
          </Col>
        </Row>

        {/* Les actions pilotent toutes le photometre : elles vivent avec lui
            plutot que dans une carte separee. */}
        <Divider style={{ margin: '12px 0' }} />

        <Space wrap>
          {deviceStatus.CanDoWatertest && (
            // Volontairement secondaire : en type="primary" ce bouton etait
            // l'action dominante de l'ecran et se lisait comme un lancement
            // d'analyse d'eau. Il s'agit d'un test de l'appareil.
            <Button
              icon={<PlayCircleOutlined />}
              onClick={() => setShowTestModal(true)}
            >
              Test de l'appareil
            </Button>
          )}

          {deviceStatus.CanDoCalibrate && (
            <Button icon={<ToolOutlined />} onClick={handleCalibrate}>
              Calibrer
            </Button>
          )}

          {deviceStatus.CanDoUpdate && (
            <Button
              type="primary"
              danger
              icon={<SyncOutlined />}
              onClick={handleUpdateFirmware}
            >
              Mettre à jour le firmware
            </Button>
          )}
        </Space>
      </Card>
    );
  };

  const changerDisqueDefaut = (nom) => {
    const reagent = testOptions.ReagentTypes.find(r => r.Name === nom);
    setDisqueDefaut(ecrireDisqueDefaut(reagent));
    message.success(`Disque par défaut : ${reagent.Name}`);
  };

  const renderConfiguration = () => {
    // Sans reponse du service on ne connait pas la gamme de l'appareil :
    // on se contente de rappeler le reglage memorise.
    const disquesDisponibles = testOptions && Array.isArray(testOptions.ReagentTypes)
      ? testOptions.ReagentTypes
      : null;
    const disqueReconnu = disquesDisponibles
      ? resoudreDisque(disquesDisponibles, disqueDefaut)
      : null;

    return (
      <Card title="Configuration" size="small">
        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          <Text>
            Disque utilisé pour les analyses lancées depuis un formulaire.
            Le disque 203 couvre le chlore, le brome et le sel.
          </Text>

          {disquesDisponibles ? (
            <Select
              style={{ width: '100%', maxWidth: 420 }}
              value={disqueReconnu ? disqueReconnu.Name : disqueDefaut.name}
              onChange={changerDisqueDefaut}
            >
              {disquesDisponibles.map(reagent => (
                <Option key={reagent.Name} value={reagent.Name}>
                  {reagent.Name}
                </Option>
              ))}
            </Select>
          ) : (
            <Text strong>{disqueDefaut.name}</Text>
          )}

          {!disquesDisponibles && (
            <Text type="secondary">
              La liste des disques est fournie par l'appareil : branchez le
              photomètre pour pouvoir changer ce réglage.
            </Text>
          )}

          {disquesDisponibles && !disqueReconnu && (
            <Alert
              type="warning"
              showIcon
              message="Disque introuvable"
              description={`L'appareil ne propose pas « ${disqueDefaut.name} ». Choisissez un disque de la liste avant de lancer une analyse depuis un formulaire.`}
            />
          )}
        </Space>
      </Card>
    );
  };

  const renderTestResults = () => {
    if (!testResults || !testResults.Results) return null;

    const columns = [
      {
        title: 'Paramètre',
        dataIndex: 'Name',
        key: 'name',
      },
      {
        title: 'Valeur',
        dataIndex: 'Value',
        key: 'value',
        render: (value, record) => (
          <Space>
            <Text>{value.toFixed(record.Decimals)}</Text>
            <Text type="secondary">{record.Units}</Text>
            <Tag color={record.State === '=' ? 'green' : record.State === '+' ? 'red' : 'orange'}>
              {record.State}
            </Tag>
          </Space>
        ),
      },
      {
        title: 'Facteur',
        dataIndex: 'FactorCode',
        key: 'factor',
        render: (code) => service.getFactorName(code),
      },
      {
        title: 'Heure',
        dataIndex: 'TimeStamp',
        key: 'timestamp',
      },
    ];

    return (
      <Table
        columns={columns}
        dataSource={testResults.Results}
        pagination={false}
        size="small"
        rowKey="Tag"
      />
    );
  };

  if (!service) {
    return (
      <div style={{ padding: '24px', textAlign: 'center' }}>
        <Spin size="large" />
        <div style={{ marginTop: 16 }}>
          <Text>Initialisation du service WaterLink...</Text>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      <Title level={2}>
        Gestion Spintouch
        <Tag
          color={isElectron ? 'blue' : 'green'}
          style={{ marginLeft: 16 }}
          icon={isElectron ? <DesktopOutlined /> : <GlobalOutlined />}
        >
          {isElectron ? 'Mode Logiciel' : 'Mode Navigateur'}
        </Tag>
      </Title>

      {/* Les premiers retours terrain montrent que des clients arrivent ici en
          pensant y lancer une analyse d'eau. On lève l'ambiguïté et on donne le
          chemin vers le bon parcours. */}
      <Alert
        type="warning"
        showIcon
        style={{ marginBottom: 16 }}
        message="Cet écran ne sert pas à analyser l'eau"
        description="Il permet de configurer et d'entretenir le photomètre Spin Touch : état de l'appareil, calibrage, mise à jour du firmware. Pour analyser un échantillon et obtenir des recommandations de traitement, utilisez Analyse."
        action={
          <Button
            size="small"
            type="primary"
            onClick={() => { window.location.href = '/forms'; }}
          >
            Faire une analyse
          </Button>
        }
      />

      <Spin spinning={loading}>
        <Row gutter={[16, 16]}>
          {/* Etat des deux maillons : un clic deplie le detail correspondant */}
          <Col span={24}>
            <Space style={{ marginBottom: 8 }}>
              <Button
                icon={<ReloadOutlined />}
                onClick={loadInitialData}
                loading={loading}
              >
                Actualiser
              </Button>
              {derniereVerif && (
                <Text type="secondary">
                  Dernière vérification : {derniereVerif.toLocaleTimeString()}
                </Text>
              )}
            </Space>
          </Col>

          <Col xs={24} sm={12}>
            {renderEncart(
              'waterlink',
              'Logiciel WaterLink Connect',
              'Le pilote installé sur ce poste',
              etatWaterLink()
            )}
          </Col>

          <Col xs={24} sm={12}>
            {renderEncart(
              'spintouch',
              'Photomètre Spin Touch',
              "L'appareil branché en USB",
              etatSpintouch()
            )}
          </Col>

          {/* Detail de l'encart selectionne */}
          <Col span={24}>
            {detailOuvert === 'waterlink' ? renderDetailWaterLink() : renderDeviceStatus()}
          </Col>

          <Col span={24}>
            {renderConfiguration()}
          </Col>

          {/* Alertes */}
          {deviceStatus?.Status === 4 && (
            <Col span={24}>
              <Alert
                message="Mise à jour requise"
                description="Une mise à jour du firmware est obligatoire pour utiliser l'appareil."
                type="error"
                showIcon
                action={
                  <Button size="small" danger onClick={handleUpdateFirmware}>
                    Mettre à jour maintenant
                  </Button>
                }
              />
            </Col>
          )}

          {deviceStatus?.Status === 2 && (
            <Col span={24}>
              <Alert
                message="Mise à jour en cours"
                description="Ne débranchez pas l'appareil pendant la mise à jour. Cette opération peut prendre 5-7 minutes."
                type="warning"
                showIcon
              />
            </Col>
          )}
        </Row>
      </Spin>

      {/* Modal de test d'eau */}
      <Modal
        title="Lancer un test d'eau"
        open={showTestModal}
        onCancel={() => setShowTestModal(false)}
        onOk={handleRunTest}
        okText="Lancer le test"
        cancelText="Annuler"
        okButtonProps={{ disabled: selectedReagent == null || selectedSample == null || testInProgress }}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <div>
            <Text strong>Sélectionner le type de réactif:</Text>
            <Select
              style={{ width: '100%', marginTop: 8 }}
              placeholder="Choisir un réactif"
              value={selectedReagent}
              onChange={setSelectedReagent}
            >
              {testOptions?.ReagentTypes?.map(reagent => (
                <Option key={reagent.Value} value={reagent.Value}>
                  {reagent.Name}
                </Option>
              ))}
            </Select>
          </div>

          <div>
            <Text strong>Sélectionner le type d'échantillon:</Text>
            <Select
              style={{ width: '100%', marginTop: 8 }}
              placeholder="Choisir un échantillon"
              value={selectedSample}
              onChange={setSelectedSample}
              disabled={selectedReagent == null}
            >
              {getCompatibleSamples().map(sample => (
                <Option key={sample.Value} value={sample.Value}>
                  {sample.Name}
                </Option>
              ))}
            </Select>
          </div>

          {testInProgress && (
            <div>
              <Text strong>Test en cours...</Text>
              <Progress percent={testProgress} />
            </div>
          )}
        </Space>
      </Modal>

      {/* Modal des résultats */}
      <Modal
        title="Résultats du test"
        open={showResultsModal}
        onCancel={() => setShowResultsModal(false)}
        footer={[
          <Button key="close" onClick={() => setShowResultsModal(false)}>
            Fermer
          </Button>
        ]}
        width={800}
      >
        {renderTestResults()}
      </Modal>

    </div>
  );
};

export default SpintouchManagementAdaptive;
